import { FC, useEffect, useState } from 'react';
import Text from '@deprecated-ui/common/Text';
import Flex from '@deprecated-ui/common/Flex';
import If from 'global/ErrorHandlers/If';
import { formatNumber, formatPercentage } from 'utils/number';
import useIsUserSyariah from 'global/TradingModal/hooks/useIsUserSyariah';
import {
  OrderSecuritiesKindType,
  OrderStateItem,
  PlatformOrderTypes,
} from 'global/Securities/types/store/order';
import DayTradeBadge from 'features/day-trade/components/Badge';
import { useBondsModalStore } from 'global/TradingModal/RealTradingModal/ModalBonds/stores/useBondsModalStore';
import NonSyariahBadge from 'features/syariah/components/NonSyariahBadge';
import { OrderStatus } from 'global/Securities/types/transaction';
import { useGetOrderList } from 'lib/queries/trade';
import transform from 'utils/securities/transformData';
import useOrderDetailStore from 'global/Securities/stores/useOrderDetailStore';
import { OrderAction } from 'global/Securities/types/api/orderList';
import clsx from 'clsx';
import tradingDataStore from 'lib/stores/tradingData';
import { AutoOrderBadge } from '../OrderTable/styled';
import {
  CustomText,
  OrderRow,
  OrdersTable,
  TableContent,
  TableHeader,
} from './styled';

const Order: FC<{
  item: OrderStateItem;
  onClick: (item: OrderStateItem) => void;
}> = ({ item, onClick }) => {
  const isSyariahUser = useIsUserSyariah();

  const isBondOrder = item.securities_kind === OrderSecuritiesKindType.Bond;
  const isMarketOrder = item.platform_order_type === PlatformOrderTypes.FAK;

  const amount = (() => {
    if (isBondOrder) return '-';

    if (item.trailing_stop) {
      return formatNumber(item.amount_invested, true, '0,0');
    }

    if (isMarketOrder) {
      if (
        [
          OrderStatus.Rejected,
          OrderStatus.Pending,
          OrderStatus.Open,
          OrderStatus.Amended,
        ].includes(item.status)
      ) {
        return '-';
      }
      if (item.status === OrderStatus.Withdrawn) {
        return formatNumber(
          item.amount_invested + item.amount_fee,
          true,
          '0,0',
        );
      }
    }

    return formatNumber(item.amount_invested_fee, true, '0,0');
  })();

  const price = (() => {
    if (item.securities_kind === OrderSecuritiesKindType.Bond) {
      return formatPercentage(item.price);
    }
    if (item.price_order) return formatNumber(item.price, true, '0,0');

    return '-';
  })();

  const gtc = (() => {
    if (isBondOrder) {
      return '-';
    }

    if (isMarketOrder) return 'FAK';

    return item.gtc ? 'GTC' : 'DAY';
  })();

  const getStatusText = () => {
    if (item.status === OrderStatus.Succeed) return 'Success';
    return item.status;
  };

  const statusText = getStatusText();

  const getOrderStatusClass = (status: string) => {
    if (status === OrderStatus.RequestAmend) return 'request_amend';
    return status.toLowerCase();
  };

  const statusClass = getOrderStatusClass(statusText);

  return (
    <OrderRow onClick={() => onClick(item)}>
      <Flex align="center" width="114px">
        <CustomText
          className={`action-content ${
            item.action === OrderAction.Buy ? 'buy' : 'sell'
          }`}
        >
          {item.action} {item.symbol.toUpperCase()}
        </CustomText>
        <If condition={Boolean(item.label && item.label !== 'MO')}>
          <AutoOrderBadge>{item.label}</AutoOrderBadge>
        </If>
        <If condition={item.day_trade_is_day_trade}>
          <DayTradeBadge label={item.day_trade_force_sell_event} />
        </If>
        <If
          condition={isSyariahUser && item.is_sharia === false && !isBondOrder}
        >
          <NonSyariahBadge label="Non-Syariah" />
        </If>
      </Flex>
      <Text width="114px" align="right" size="12px">
        {amount}
      </Text>
      <Text width="44px" align="right" size="12px">
        {price}
      </Text>
      <Text width="44px" align="right" size="12px">
        {gtc}
      </Text>
      <div style={{ width: 84 }}>
        <CustomText className={clsx([statusClass])}>{statusText}</CustomText>
      </div>
    </OrderRow>
  );
};

const OrderList: FC<{ symbol: string; isDayTrade: boolean }> = ({
  symbol,
  isDayTrade,
}) => {
  const [orders, setOrders] = useState<OrderStateItem[]>([]);

  const { data } = useGetOrderList(0, symbol);

  const loadDetail = useOrderDetailStore((s) => s.loadOrderDetail);

  const openBondOrderModal = useBondsModalStore((s) => s.openOrderModal);

  useEffect(() => {
    if (data && data.length > 0) {
      const newOrders = data
        .filter((d) =>
          isDayTrade ? d.day_trade.is_day_trade : !d.day_trade.is_day_trade,
        )
        .map((d) => transform.orderDetail(d));
      setOrders(newOrders);
    }

    // subscribe to ws change
    tradingDataStore.subscribe(
      (s) => s.orderHistory.data,
      (orderHistory) => {
        const symbolOrderHistory = orderHistory.filter((item) =>
          item.symbol === symbol && isDayTrade
            ? item.day_trade_is_day_trade
            : !item.day_trade_is_day_trade,
        );
        setOrders(symbolOrderHistory);
      },
    );
  }, [data]);

  const handleRow = (item) => {
    if (item.securities_kind === OrderSecuritiesKindType.Bond) {
      openBondOrderModal(item.orderid);
      return;
    }

    loadDetail(item.orderid);
  };

  if (orders.length === 0) return null;

  return (
    <TableContent>
      <Text padding="18px 16px" size={12} weight={700}>
        Order List
      </Text>
      <TableHeader>
        <Flex height="32px" align="center" padding="0 16px" style={{ gap: 16 }}>
          <Text width="114px" size="12px">
            Action
          </Text>
          <Text width="114px" align="right" size="12px">
            Amount
          </Text>
          <Text width="44px" align="right" size="12px">
            Price
          </Text>
          <Text width="44px" align="right" size="12px">
            Expiry
          </Text>
          <Text width="84px" size="12px">
            Status
          </Text>
        </Flex>
      </TableHeader>
      <OrdersTable>
        {orders.map((order) => (
          <Order item={order} key={order.orderid} onClick={handleRow} />
        ))}
      </OrdersTable>
    </TableContent>
  );
};

export default OrderList;
